import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Col,
  DatePicker,
  Flex,
  Form,
  Image,
  Input,
  message,
  Pagination,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tooltip,
} from 'antd';
import type { TableColumnType } from 'antd';
import './index.css';
import { useRequest } from 'ahooks';
import { getImproperList, operateViolation } from '@/api/contentReview';
import {
  Artwork,
  ArtworkListResponse,
  ArtworkListSearchParams,
} from '@/types/contentReview';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { isMobileAtom } from '@/jotai/atoms/userAtom';
import dayjs from 'dayjs';
import { validateText } from '@/utils/validate';
import { FORMAT_DATE } from '@/types/common';
import {
  SearchOutlined,
  ReloadOutlined,
  CheckOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { commonOptionsAtom } from '@/atoms/commonOptions';

// 接口文档地址
// https://supptech.sg.larksuite.com/wiki/Nobsw0FhdilBpak9V2nlUc29gxh

export const CONTENT_REVIEW_IDS_KEY = 'content_review_ids';
export const CONTENT_REVIEW_INDEX_KEY = 'content_review_index';

enum ClassificationEnum {
  '用户头像',
  '用户昵称',
  '群图片',
  '表情包',
  '个人简介',
  '其他职业',
  '话题封面' = 10,
}

const arr = [
  ...new Array(6).fill(0).map((_, index) => [index, ClassificationEnum[index]] as const),
  [10, ClassificationEnum[10]] as const,
];
const ClassificationMap = new Map(arr);

const ContentReview: React.FC = () => {
  const [form] =
    Form.useForm<
      Omit<ArtworkListSearchParams, 'currentPage' | 'pageSize' | 'reviewStatus'>
    >();
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [selectedRows, setSelectedsRows] = useState<any[]>([]);
  const isMobile = useAtomValue(isMobileAtom);
  const navigate = useNavigate();
  const pageSize = isMobile ? 10 : 20;
  const [page, setPage] = useState(1);
  const [messageApi, contextHolder] = message.useMessage();
  const [searchParams, setSearchParams] = useSearchParams();

  const { userTypes } = useAtomValue(commonOptionsAtom);

  const userTypesMap = useMemo(() => {
    const arr = userTypes.map((item) => [item.value, item.text]);
    return new Map(arr as any[]);
  }, [userTypes]);

  useEffect(() => {
    const formValues: Record<string, any> = {};
    searchParams.forEach((value, key) => {
      if (key === 'channelDictId') {
        formValues[key] = parseInt(value);
      } else if (key === 'violationStatus') {
        formValues[key] = parseInt(value);
      } else {
        formValues[key] = value;
      }
    });
    form.setFieldsValue(formValues);
    const page = searchParams.get('page') || '1';
    setPage(parseInt(page));
  }, [searchParams, form]);

  const {
    data,
    loading,
    refresh: refetch,
  } = useRequest(
    async () => {
      const formValues = form.getFieldsValue();
      const timeRange = formValues.timeRange;
      const params: ArtworkListSearchParams = {
        currentPage: page,
        pageSize,
        ...formValues,
      };
      // 删除 timeRange，转为 startTime & endTime
      delete params.timeRange;
      if (!params.channelDictId) {
        delete params.channelDictId;
      }
      if (timeRange?.length === 2) {
        params.startTime = dayjs(timeRange[0]).format(FORMAT_DATE);
        params.endTime = dayjs(timeRange[1]).format(FORMAT_DATE);
      }
      const resp = await getImproperList(params);
      return {
        list: resp.data.records,
        total: resp.data.total,
      };
    },
    {
      refreshDeps: [page, searchParams],
    }
  );

  const checkArtworkIds = () => {
    if (!form.getFieldValue('artworkIds')) {
      return true;
    }
    const artworkIds = form.getFieldValue('artworkIds') as string;
    if (!validateText(artworkIds, true)) {
      messageApi.error('Please use English commas to separate artwork IDs');
      return false;
    }
    // 检查数量是否超过 10
    const artworkIdsArray = artworkIds.split(',');
    if (artworkIdsArray.length > 10) {
      messageApi.error('The number of artwork IDs cannot exceed 10');
      return false;
    }
    return true;
  };

  const checkTags = () => {
    if (!form.getFieldValue('tags')) {
      return true;
    }
    const tags = form.getFieldValue('tags') as string;
    if (!validateText(tags, true)) {
      messageApi.error('Please use English commas to separate tags');
      return false;
    }
    // 检查数量是否超过 20
    const tagsArray = tags.split(',');
    if (tagsArray.length > 20) {
      messageApi.error('The number of tags cannot exceed 20');
      return false;
    }
    return true;
  };

  const onFinish = () => {
    if (!checkArtworkIds() || !checkTags()) {
      return;
    }
    const params = new URLSearchParams();
    const values = form.getFieldsValue();
    const timeRange = values.timeRange;

    // 处理时间范围
    if (timeRange?.length === 2) {
      params.set('createTimeStart', timeRange[0].valueOf().toString());
      params.set('createTimeEnd', timeRange[1].valueOf().toString());
    }

    // 处理其他参数
    Object.entries(values).forEach(([key, value]) => {
      if (
        value !== undefined &&
        value !== null &&
        value !== '' &&
        key !== 'timeRange'
      ) {
        // 对于 channelDictId，只有当它有值时才传递
        if (key === 'channelDictId') {
          if (value) {
            params.set(key, value.toString());
          }
        } else {
          params.set(key, value.toString());
        }
      }
    });
    params.set('page', '1');
    setSearchParams(params);
    if (page !== 1) {
      setPage(1);
      return;
    }
    void refetch();
  };

  const columns: TableColumnType<Artwork>[] = [
    {
      title: 'user_id',
      dataIndex: 'userId',
      key: 'userId',
    },
    {
      title: '识别日期',
      dataIndex: 'createTime',
      key: 'createTime',
      render: (_) => {
        return _ || '-';
      },
    },
    {
      title: '内容分类',
      dataIndex: 'contentClassification',
      key: 'contentClassification',
      render: (_) => {
        return (
          <Space size="small" direction="vertical">
            <span>{ClassificationEnum[_] || '-'}</span>
            {/* <Tag color="blue">{record.categoryFirst}</Tag>
            <Tag color="green">{record.categorySecond}</Tag> */}
          </Space>
        );
      },
    },
    {
      title: '内容详情',
      dataIndex: 'violationContent',
      key: 'violationContent',
      width: '120px',
      render: (_, record) => {
        if (
          [
            ClassificationEnum['用户头像'],
            ClassificationEnum['群图片'],
            ClassificationEnum['表情包'],
            ClassificationEnum['话题封面'],
          ].includes(record.contentClassification)
        ) {
          return (
            <Image
              key={_}
              src={_}
              width={50}
              height={50}
              loading="lazy"
              placeholder={
                <div
                  style={{
                    width: 50,
                    height: 50,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#f5f5f5',
                    borderRadius: '4px',
                  }}
                >
                  <span style={{ fontSize: '12px', color: '#999' }}>
                    Loading
                  </span>
                </div>
              }
              style={{
                objectFit: 'cover',
                borderRadius: '4px',
              }}
              preview={{
                src: _,
              }}
            />
          );
        }

        if (_?.length > 50) {
          return <Tooltip title={_}>{`${_.slice(0, 50)}...`}</Tooltip>;
        }
        return <Space>{_}</Space>;
      },
    },
    {
      title: '是否违规',
      dataIndex: 'violationStatus',
      key: 'violationStatus',
      render: (_) => (_ ? '是' : '否'),
    },
    {
      title: '用户SUPP ID',
      dataIndex: 'suppId',
      key: 'suppId',
      render: (_) => _ || '-',
    },
    {
      title: '用户昵称',
      dataIndex: 'nickName',
      key: 'nickName',
      render: (_) => _ || '-',
    },
    {
      title: '名字',
      dataIndex: 'name',
      key: 'name',
      render: (_) => _ || '-',
    },
    {
      title: '用户类型',
      dataIndex: 'showType',
      key: 'showType',
      render: (_) => userTypesMap.get(_) || '-',
    },
    {
      title: '操作',
      key: 'operation',
      width: 120,
      fixed: 'right',
      render: (_, record, index) => (
        <Space size="small" direction="vertical">
          <Button
            type="link"
            disabled={!record.violationStatus}
            onClick={() => handleViolation(record, 0)}
          >
            恢复为正常
          </Button>
          <Button
            type="link"
            disabled={record.violationStatus}
            onClick={() => handleViolation(record, 1)}
          >
            确认违规
          </Button>
        </Space>
      ),
    },
  ];

  const handleViolation = async (record, status: 0 | 1) => {
    const violationList = Array.isArray(record) ? record : [record];
    const res = await operateViolation({
      violationList,
      violationStatus: status,
    });

    if (res.status !== 200) {
      return message.error(res.message);
    }

    refetch();
  };

  const batchApproveDisabled = useMemo(() => {
    if (!selectedRows.length) return true;
    const unImproperRow = selectedRows.find(
      (item) => item.violationStatus === 0
    );
    return !!unImproperRow;
  }, [selectedRows]);

  const handleBatchApproveClick = useCallback(async () => {
    handleViolation(selectedRows, 0);
  }, [selectedRows]);

  const batchRejectDisabled = useMemo(() => {
    if (!selectedRows.length) return true;
    const improperRow = selectedRows.find((item) => item.violationStatus === 1);
    return !!improperRow;
  }, [selectedRows]);

  const handleBatchRejectClick = useCallback(() => {
    handleViolation(selectedRows, 1);
  }, [selectedRows]);

  const { RangePicker } = DatePicker;

  const handleReset = () => {
    const params = new URLSearchParams();
    setSearchParams(params);
    setPage(1);
    form.resetFields();
    refetch();
  };

  return (
    <div className="content-review-container">
      {contextHolder}
      <Form form={form} name="searchForm" onFinish={onFinish} size="small">
        <Row gutter={[8, 8]} style={{ width: '100%' }}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item
              label="Supp Id"
              name="suppId"
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
            >
              <Input placeholder="请输入" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item
              label="user_id"
              name="userId"
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
            >
              <Input placeholder="请输入" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item
              label="内容分类"
              name="contentClassification"
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
            >
              <Select
                allowClear
                placeholder="请选择"
                options={[...ClassificationMap.entries()].map((info) => ({
                  label: info[1],
                  value: String(info[0]),
                }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item
              label="识别日期"
              name="timeRange"
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
            >
              <RangePicker format="YYYY-MM-DD" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item
              label="敏感类型"
              name="sensitiveType"
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
            >
              <Select
                allowClear
                placeholder="请选择"
                defaultValue={String(0)}
                options={[
                  { label: '全部', value: 0 },
                  { label: '图片', value: 1 },
                  { label: '文字', value: 2 },
                ].map((item) => ({
                  ...item,
                  value: String(item.value),
                }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item
              label="是否违规"
              name="violationStatus"
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
            >
              <Select
                allowClear
                placeholder="请选择"
                options={[
                  { label: '是', value: 1 },
                  { label: '否', value: 0 },
                ]}
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                borderTop: '1px solid #f0f0f0',
                marginTop: '8px',
                paddingTop: '16px',
              }}
            >
              <Space size="middle">
                <Button onClick={handleReset} icon={<ReloadOutlined />}>
                  重置
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SearchOutlined />}
                >
                  搜索
                </Button>
              </Space>
            </div>
          </Col>
        </Row>
      </Form>

      <div className="table-container">
        <div className="table-header" style={{ justifyContent: 'flex-end' }}>
          <Space size="middle">
            <Popconfirm
              title="你确定要将这些内容恢复为正常吗?"
              onConfirm={handleBatchApproveClick}
            >
              <Button
                type="primary"
                disabled={batchApproveDisabled}
                icon={<CheckOutlined />}
              >
                恢复为正常
              </Button>
            </Popconfirm>
            <Popconfirm
              title="你确定要将这些内容确认为违规吗?"
              onConfirm={handleBatchRejectClick}
              okType="danger"
            >
              <Button
                type="primary"
                danger
                disabled={batchRejectDisabled}
                icon={<CloseOutlined />}
              >
                确认为违规
              </Button>
            </Popconfirm>
          </Space>
        </div>
        <Table
          size="small"
          loading={loading}
          rowKey="id"
          key="improper"
          columns={columns}
          dataSource={data?.list || []}
          scroll={{ x: 'max-content' }}
          rowSelection={
            isMobile
              ? undefined
              : {
                  selectedRowKeys: selectedKeys,
                  type: 'checkbox',
                  fixed: true,
                  onChange: (selectedRowKeys, selectedRows) => {
                    setSelectedKeys(selectedRowKeys as string[]);
                    setSelectedsRows(selectedRows);
                  },
                }
          }
          pagination={false}
        />

        <Flex
          className="table-pagination"
          justify="space-between"
          style={{ flexDirection: isMobile ? 'row-reverse' : 'row' }}
        >
          <Pagination
            size={isMobile ? 'small' : 'default'}
            total={data?.total}
            pageSize={pageSize}
            current={page}
            onChange={(page) => {
              setPage(page);
              const params = new URLSearchParams(searchParams);
              params.set('page', page.toString());
              setSearchParams(params);
            }}
            showSizeChanger={false}
          />
        </Flex>
      </div>
    </div>
  );
};

export default ContentReview;
